import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Loader2, Plus, Trash2, Tag, LogOut, Gift, HardDrive, Film, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Navbar } from "@/components/Navbar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mlm_admin_token";

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const [promos, setPromos] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [storage, setStorage] = useState(null);
  const [hasExample, setHasExample] = useState(false);
  const [uploadingExample, setUploadingExample] = useState(false);
  const [exampleVer, setExampleVer] = useState(0);

  const [label, setLabel] = useState("");
  const [code, setCode] = useState("");
  const [isFree, setIsFree] = useState(false);
  const [percent, setPercent] = useState(50);
  const [creating, setCreating] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setPromos([]);
  }, []);

  const loadPromos = useCallback(async () => {
    if (!token) return;
    setLoadingList(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [promosRes, storageRes] = await Promise.all([
        axios.get(`${API}/admin/promos`, { headers }),
        axios.get(`${API}/admin/storage`, { headers }),
      ]);
      setPromos(promosRes.data);
      setStorage(storageRes.data);
      try {
        const ex = await axios.get(`${API}/example-video`);
        setHasExample(!!ex.data.exists);
      } catch {}
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Session expired, please log in again");
        logout();
      }
    } finally {
      setLoadingList(false);
    }
  }, [token, logout]);

  useEffect(() => {
    loadPromos();
  }, [loadPromos]);

  const login = async () => {
    if (!password.trim()) return;
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
      toast.success("Welcome back");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Incorrect password");
    } finally {
      setLoggingIn(false);
    }
  };

  const createPromo = async () => {
    const pct = isFree ? 100 : Number(percent);
    if (!pct || pct < 1 || pct > 100) {
      toast.error("Choose a discount between 1% and 100%");
      return;
    }
    setCreating(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/promos`,
        { label: label.trim(), percent_off: pct, code: code.trim() },
        auth
      );
      toast.success(`Created code ${data.code}`);
      setLabel("");
      setCode("");
      setIsFree(false);
      setPercent(50);
      loadPromos();
    } catch (e) {
      if (e?.response?.status === 401) { logout(); return; }
      toast.error(e?.response?.data?.detail || "Could not create promo code");
    } finally {
      setCreating(false);
    }
  };

  const removePromo = async (id) => {
    try {
      await axios.delete(`${API}/admin/promos/${id}`, auth);
      toast.success("Promo code deactivated");
      loadPromos();
    } catch (e) {
      toast.error("Could not deactivate");
    }
  };

  const uploadExample = async (file) => {
    if (!file) return;
    setUploadingExample(true);
    try {
      const form = new FormData();
      form.append("video", file, file.name || "example.mp4");
      await axios.post(`${API}/admin/example-video`, form, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setHasExample(true);
      setExampleVer((v) => v + 1);
      toast.success("Example video updated");
    } catch (e) {
      if (e?.response?.status === 401) { logout(); return; }
      toast.error(e?.response?.data?.detail || "Could not upload video");
    } finally {
      setUploadingExample(false);
    }
  };

  const removeExample = async () => {
    try {
      await axios.delete(`${API}/admin/example-video`, auth);
      setHasExample(false);
      setExampleVer((v) => v + 1);
      toast.success("Example video removed");
    } catch (e) {
      toast.error("Could not remove video");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-obsidian bg-grain">
        <Navbar />
        <div className="max-w-md mx-auto px-5 pt-40 pb-20 text-center">
          <span className="inline-grid place-items-center w-16 h-16 rounded-full border border-gold/40 text-gold mb-6">
            <Lock className="w-7 h-7" />
          </span>
          <h1 className="font-serif text-3xl text-gold-light">Admin access</h1>
          <div className="glass-card rounded-2xl p-7 mt-8">
            <Input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && login()}
              placeholder="Admin password"
              className="bg-obsidian/60 border-gold/20 focus-visible:ring-gold/40 text-slate-100 h-12 text-center"
            />
            <Button
              data-testid="admin-login-button"
              onClick={login}
              disabled={loggingIn}
              className="w-full mt-4 bg-gold hover:bg-gold-hover text-obsidian font-semibold rounded-full h-12"
            >
              {loggingIn ? <Loader2 className="w-5 h-5 animate-spin" /> : "Log in"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian bg-grain">
      <Navbar />
      <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-28 pb-20">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl sm:text-4xl text-gold-light">Promo codes</h1>
            <p className="text-slate-400 mt-2">Create free or discounted recording codes for family.</p>
          </div>
          <Button data-testid="admin-logout-button" onClick={logout} variant="outline" className="rounded-full border-gold/30 text-slate-300 bg-transparent hover:bg-gold/10 hover:text-gold">
            <LogOut className="w-4 h-4 mr-1" /> Log out
          </Button>
        </div>

        {/* Storage meter */}
        {storage && (
          <div data-testid="storage-meter" className="glass-card rounded-2xl p-6 sm:p-7 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-sans font-semibold text-lg text-gold-light flex items-center gap-2"><HardDrive className="w-5 h-5" /> Storage</h2>
              <span className="text-sm text-slate-400">
                {(storage.used_bytes / (1024 * 1024)).toFixed(1)} / {(storage.limit_bytes / (1024 * 1024)).toFixed(0)} MB
              </span>
            </div>
            <div className="h-3 bg-obsidian/70 rounded-full overflow-hidden border border-gold/10">
              <div
                data-testid="storage-bar"
                className={`h-full transition-all duration-700 ${storage.percent_used >= 90 ? "bg-destructive" : "bg-gradient-to-r from-gold to-amber-400"}`}
                style={{ width: `${Math.min(storage.percent_used, 100)}%` }}
              />
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-1 mt-4 text-sm text-slate-400">
              <span><span className="text-gold-light font-semibold" data-testid="storage-count">{storage.count}</span> stored</span>
              <span>~<span className="text-gold-light font-semibold" data-testid="storage-remaining">{storage.est_remaining_videos}</span> more videos fit</span>
              <span>{storage.percent_used}% used</span>
            </div>
            {storage.percent_used >= 90 && (
              <p className="text-xs text-destructive mt-3">You're running low on space — delete old messages in the vault to free room.</p>
            )}
          </div>
        )}

        {/* Example video */}
        <div data-testid="example-video-card" className="glass-card rounded-2xl p-6 sm:p-7 mb-8">
          <h2 className="font-sans font-semibold text-lg text-gold-light mb-2 flex items-center gap-2"><Film className="w-5 h-5" /> Home page example video</h2>
          <p className="text-sm text-slate-400 mb-5">Upload a short clip shown in the "See an example" section on the home page.</p>
          {hasExample && (
            <video
              key={exampleVer}
              data-testid="admin-example-preview"
              src={`${API}/example-video/stream?v=${exampleVer}`}
              controls
              playsInline
              className="w-full max-w-md rounded-xl bg-black aspect-video object-contain mb-5"
            />
          )}
          <div className="flex flex-wrap items-center gap-3">
            <label data-testid="example-upload-label" className="inline-flex items-center gap-2 cursor-pointer bg-gold hover:bg-gold-hover text-obsidian font-semibold rounded-full h-11 px-6 transition-colors">
              {uploadingExample ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-4 h-4" />}
              {hasExample ? "Replace video" : "Upload video"}
              <input
                data-testid="example-upload-input"
                type="file"
                accept="video/*"
                className="hidden"
                disabled={uploadingExample}
                onChange={(e) => uploadExample(e.target.files?.[0])}
              />
            </label>
            {hasExample && (
              <Button data-testid="example-remove-button" onClick={removeExample} variant="outline" className="rounded-full border-destructive/40 text-destructive bg-transparent hover:bg-destructive/10">
                <Trash2 className="w-4 h-4 mr-1" /> Remove
              </Button>
            )}
          </div>
        </div>

        {/* Create */}
        <div className="glass-card rounded-2xl p-6 sm:p-7 mb-8">
          <h2 className="font-sans font-semibold text-lg text-gold-light mb-5 flex items-center gap-2"><Plus className="w-5 h-5" /> New promo code</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-slate-200">Label (for your reference)</Label>
              <Input data-testid="promo-label-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Mum's free code" className="mt-2 bg-obsidian/60 border-gold/20 focus-visible:ring-gold/40 text-slate-100" />
            </div>
            <div>
              <Label className="text-slate-200">Custom code (optional)</Label>
              <Input data-testid="promo-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Auto-generated if blank" className="mt-2 bg-obsidian/60 border-gold/20 focus-visible:ring-gold/40 text-slate-100 font-mono" />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-5 mt-5">
            <div className="flex items-center gap-3">
              <Switch data-testid="promo-free-switch" checked={isFree} onCheckedChange={setIsFree} />
              <span className="text-sm text-slate-200 flex items-center gap-1"><Gift className="w-4 h-4 text-gold" /> Free (100% off)</span>
            </div>
            {!isFree && (
              <div className="flex items-center gap-2">
                <Label className="text-slate-200 whitespace-nowrap">% off</Label>
                <Input data-testid="promo-percent-input" type="number" min={1} max={100} value={percent} onChange={(e) => setPercent(e.target.value)} className="w-24 bg-obsidian/60 border-gold/20 focus-visible:ring-gold/40 text-slate-100" />
              </div>
            )}
            <Button data-testid="promo-create-button" onClick={createPromo} disabled={creating} className="sm:ml-auto bg-gold hover:bg-gold-hover text-obsidian font-semibold rounded-full h-11 px-6">
              {creating ? <Loader2 className="w-5 h-5 animate-spin" /> : "Create code"}
            </Button>
          </div>
        </div>

        {/* List */}
        <div className="glass-card rounded-2xl p-6 sm:p-7">
          <h2 className="font-sans font-semibold text-lg text-gold-light mb-5 flex items-center gap-2"><Tag className="w-5 h-5" /> Active codes</h2>
          {loadingList ? (
            <div className="text-center py-8"><Loader2 className="w-6 h-6 text-gold animate-spin mx-auto" /></div>
          ) : promos.length === 0 ? (
            <p data-testid="promo-empty" className="text-slate-500 text-sm py-6 text-center">No promo codes yet. Create one above.</p>
          ) : (
            <div className="space-y-3">
              {promos.map((p, i) => (
                <div key={p.id} data-testid={`promo-item-${i}`} className="flex items-center justify-between gap-3 bg-obsidian/50 border border-gold/15 rounded-xl px-4 py-3">
                  <div className="min-w-0">
                    <span className="font-mono text-gold text-lg tracking-wider">{p.code}</span>
                    <span className="ml-3 text-sm text-slate-300">{p.percent_off === 100 ? "Free" : `${p.percent_off}% off`}</span>
                    {p.label && <p className="text-xs text-slate-500 truncate mt-0.5">{p.label}</p>}
                  </div>
                  <Button data-testid={`promo-delete-${i}`} onClick={() => removePromo(p.id)} variant="outline" className="shrink-0 rounded-full border-destructive/40 text-destructive bg-transparent hover:bg-destructive/10">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
